import { DownOutlined, UpOutlined } from '@ant-design/icons';
import {
  Col,
  Row,
  Table,
  TableProps,
  message,
  Grid,
  Tooltip,
  Badge,
} from 'antd';
import { SorterResult } from 'antd/lib/table/interface';
import config from 'config';
import {
  DonationListFilterInput,
  DonationListSort,
  DonationOutput,
  Order,
  PermissionsEnum,
  useGetDonationListQuery,
} from 'gql/types';
import { Button, TableSearchInput, Spin } from 'modules/common/components';
import { FilterChips } from 'modules/common/components/FilterChips';
import { useDebounce, useFilter, useSort } from 'modules/common/hooks';
import { useRegionalFilterInit } from 'modules/common/hooks/useRegionalFilterInit';
import { getColumns } from 'modules/donations/components/DonationsTable/columns';
import {
  createDonationsTableChipsConfig,
  excludedFilterFields,
} from 'modules/donations/configs/donationsTableFilters';
import { useUploadDonationsModalContext } from 'modules/donations/contexts/UploadDonationsModal';
import { usePagination } from 'modules/navigation/hooks/usePagination';
import DonationProductDescriptions from 'modules/products/components/DonationProductsTable';
import { useWoocommerceCategories } from 'modules/products/hooks/useWoocommerceCategories';
import { useCurrentUser } from 'modules/user/hooks/useCurrentUser';
import { useUserPermissionsAndRegion } from 'modules/user/hooks/useUserPermissionsAndRegion';
import { FC, useEffect, useMemo } from 'react';
import { getTranslation } from 'utils/getTranslation';

import { OutcomeFlagFilter, TableColumns } from './enum';
import styles from './styles.module.scss';

const defaultOrder = [
  {
    fieldName: DonationListSort.LastDonationDate,
    order: Order.Desc,
  },
];

const { useBreakpoint } = Grid;

export const DonationsTable: FC = () => {
  const t = getTranslation('donation.DonationsTable');

  const { debounce, clearDebounce } = useDebounce();
  const pagination = usePagination();
  const sort = useSort<DonationListSort>(defaultOrder);
  const filter = useFilter<DonationListFilterInput>();

  const uploadDonationsModalContext = useUploadDonationsModalContext();

  const currentUser = useCurrentUser();
  const canAddProducts = currentUser.isCan(PermissionsEnum.AddProducts);
  const canAddDonation = currentUser.isCan(PermissionsEnum.AddNewDonation);

  const { categories, loading: categoriesLoading } = useWoocommerceCategories();

  const { assignedLocations, canViewAllRegions, hasLocationRestrictions } =
    useUserPermissionsAndRegion();

  const assignedLocationNames = useMemo(
    () => assignedLocations.map((l) => l.name),
    [assignedLocations]
  );

  const { isLoading: isRegionalFilterLoading } =
    useRegionalFilterInit<DonationListFilterInput>({
      filterHook: filter,
      filterFieldName: 'locations',
      canViewAllRegions,
      hasLocationRestrictions,
      assignedLocationNames,
      successMessage: t('regionalFilterApplied'),
    });

  const productCategories = useMemo(() => {
    if (!categories) {
      return undefined;
    }

    return categories.reduce((acc, category) => {
      return { ...acc, [category.id]: category.name };
    }, {} as Record<number, string>);
  }, [categories]);

  const chipsConfig = useMemo(
    () => createDonationsTableChipsConfig({ productCategories, t }),
    [productCategories, t]
  );

  const donationFilter = filter.getFilterObject();

  const getDonationListQuery = useGetDonationListQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      input: {
        sort: sort.options,
        filter: donationFilter,
        skip: pagination.skip,
        take: config.PAGINATION.DEFAULT_LIMIT,
      },
    },
    skip: isRegionalFilterLoading,
    onCompleted: ({ getDonationsList }) => {
      pagination.setTotal(getDonationsList.meta.total);
    },
    onError: (error) => {
      message.error(error.message);
    },
  });

  const donationList = getDonationListQuery.data?.getDonationsList
    .data as DonationOutput[];

  const onTableChange: TableProps<DonationOutput>['onChange'] = (
    _,
    filters,
    sorter,
    extra
  ) => {
    if (extra.action === 'sort') {
      const columnKey = (sorter as SorterResult<DonationOutput>)
        .columnKey as DonationListSort;
      const current = sort.options.find((o) => o.fieldName === columnKey);

      if (!current) {
        sort.setSortOption({ fieldName: columnKey, order: Order.Asc });
      } else if (current.order === Order.Asc) {
        sort.setSortOption({ fieldName: columnKey, order: Order.Desc });
      } else if (current.order === Order.Desc) {
        if (columnKey === DonationListSort.LastDonationDate) {
          sort.setSortOption({ fieldName: columnKey, order: Order.Asc });
        } else {
          sort.removeSortOption(columnKey);
        }
      }
    }

    if (extra.action === 'filter') {
      Object.entries(filters).forEach(([fieldName, value]) => {
        if (fieldName === TableColumns.outcome) {
          const outcomes = (value as string[]) || [];

          const outcomeFlagValues = Object.values(
            OutcomeFlagFilter
          ) as string[];
          const categoryIds = outcomes
            .filter((v) => !outcomeFlagValues.includes(v))
            .map(Number);

          outcomeFlagValues.forEach((flag) => {
            const flagValue = outcomes.includes(flag);
            filter.setFilterOption({
              fieldName: flag as keyof DonationListFilterInput,
              value: flagValue ? true : undefined,
            });
          });

          filter.setFilterOption({
            fieldName: 'productCategories',
            value: categoryIds.length > 0 ? categoryIds : undefined,
          });

          return;
        }

        filter.setFilterOption({
          fieldName: fieldName as keyof DonationListFilterInput,
          value,
        });
      });
    }
  };

  const handleSearch = (value: string) => {
    pagination.moveTo(1);
    filter.setFilterOption({
      fieldName: 'searchQuery',
      value,
    });
  };

  const { md } = useBreakpoint();

  const columns = useMemo(() => {
    return getColumns({
      canAddProducts,
      productCategories,
      showSorterTooltip: md,
      showFilters: donationFilter !== undefined,
      filtersValues: donationFilter,
      sort: sort?.options[0],
    });
  }, [canAddProducts, productCategories, donationFilter, sort?.options[0]]);

  useEffect(() => {
    return clearDebounce;
  }, []);

  const isLoading =
    isRegionalFilterLoading ||
    getDonationListQuery.loading ||
    categoriesLoading;

  const searchInputColumnWidth = canAddDonation ? 19 : 24;

  return (
    <>
      <Spin spinning={isLoading}>
        <Row gutter={[0, 16]}>
          <Col span={24}>
            <Row gutter={[10, 10]}>
              <Col sm={searchInputColumnWidth} span={24}>
                <TableSearchInput
                  placeholder={t('searchPlaceholder')}
                  onSearchChange={debounce(handleSearch)}
                />
              </Col>
              {canAddDonation && (
                <Col sm={5} span={24}>
                  <Button
                    onClick={() => uploadDonationsModalContext.showModal()}
                    block
                    type="primary"
                  >
                    {t('uploadCsv')}
                  </Button>
                </Col>
              )}
            </Row>
          </Col>

          <Col span={24}>
            <FilterChips<DonationListFilterInput>
              filterHook={filter}
              config={chipsConfig}
              excludeFields={excludedFilterFields}
            />
          </Col>

          <Col span={24}>
            <Table
              rowKey={({ id }) => id}
              columns={columns}
              dataSource={donationList}
              scroll={{ x: 750 }}
              onChange={onTableChange}
              pagination={pagination.config}
              expandable={{
                expandedRowRender: (donation) => {
                  return (
                    <DonationProductDescriptions
                      donation={donation}
                      pagination={false}
                      productCategories={productCategories}
                    />
                  );
                },
                rowExpandable: ({ products }) => {
                  return Boolean(products?.length);
                },
                expandIcon: ({ expanded, onExpand, record }) => {
                  const productsCount = record.products?.length || 0;

                  if (productsCount === 0) {
                    return null;
                  }

                  return (
                    <Tooltip
                      title={expanded ? t('hideProducts') : t('showProducts')}
                    >
                      <Button
                        type="text"
                        size="small"
                        icon={expanded ? <UpOutlined /> : <DownOutlined />}
                        onClick={(e) => onExpand(record, e)}
                        className={styles.expandableButton}
                      >
                        <Badge
                          count={productsCount}
                          showZero={false}
                          className={styles.expandableButtonBadge}
                        />
                      </Button>
                    </Tooltip>
                  );
                },
              }}
            />
          </Col>
        </Row>
      </Spin>
    </>
  );
};
